import { useEffect, useRef } from 'react'
import {
  useQuery,
  type UseQueryOptions,
  type UseQueryResult
} from '@tanstack/react-query'

type Callbacks<TData, TError> = {
  onSuccess?: (data: TData) => void
  onError?: (err: TError) => void
  onSettled?: (data: TData | undefined, err: TError | null) => void
}

/**
 * Drop-in replacement for v3 `useQuery` calls that still rely on
 * onSuccess / onError / onSettled. v5 removed those — this helper
 * re-implements them via useEffect keyed on dataUpdatedAt / errorUpdatedAt.
 *
 * Behavior matches v3: callbacks fire once per *fresh* fetch resolution,
 * NOT on cache hits during mount. We snapshot the mount-time timestamps via
 * refs and only fire when they advance, so re-mounting a hook on cached data
 * does not re-trigger side effects.
 *
 * Use this for direct useQuery call sites being migrated. New code should
 * write the useEffect inline against query.data / query.error.
 */
export function useQueryCallbacks<
  TQueryFnData,
  TError = Error,
  TData = TQueryFnData,
  TQueryKey extends readonly unknown[] = readonly unknown[]
>(
  options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey> &
    Callbacks<TData, TError>
): UseQueryResult<TData, TError> {
  const { onSuccess, onError, onSettled, ...queryOpts } = options
  const query = useQuery(queryOpts)
  const { data, error, dataUpdatedAt, errorUpdatedAt, status } = query

  const lastDataAt = useRef(dataUpdatedAt)
  const lastErrorAt = useRef(errorUpdatedAt)
  const lastSettledDataAt = useRef(dataUpdatedAt)
  const lastSettledErrorAt = useRef(errorUpdatedAt)

  useEffect(() => {
    if (
      dataUpdatedAt > lastDataAt.current &&
      status === 'success' &&
      data !== undefined
    ) {
      onSuccess?.(data)
    }
    lastDataAt.current = dataUpdatedAt
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUpdatedAt])

  useEffect(() => {
    if (errorUpdatedAt > lastErrorAt.current && status === 'error' && error) {
      onError?.(error)
    }
    lastErrorAt.current = errorUpdatedAt
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorUpdatedAt])

  useEffect(() => {
    const dataAdvanced = dataUpdatedAt > lastSettledDataAt.current
    const errorAdvanced = errorUpdatedAt > lastSettledErrorAt.current
    if (
      (dataAdvanced || errorAdvanced) &&
      (status === 'success' || status === 'error')
    ) {
      onSettled?.(data, error ?? null)
    }
    lastSettledDataAt.current = dataUpdatedAt
    lastSettledErrorAt.current = errorUpdatedAt
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUpdatedAt, errorUpdatedAt])

  return query
}
